import React, { useMemo, useState } from "react";
import { Button, Col, notification, Popover, Row, Typography } from "antd";
import FanOperationType from "src/consts/FanOperationType";
import PremierPlusFanSettingsModal from "./PremierPlusFanSettingsModal";
import NoticeCard from "./NoticeCard";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import PremierPlusDryerSettingsModal from "./PremierPlusDryerSettingsModal";
import PremierPlusSyncSettingsRequestDTO from "src/models/PremierPlusSyncSettingsRequestDTO";
import BinApiService from "src/api/BinApiService";
import HmiViewModal, { ViewHmi } from "./HmiView/HmiViewModal";
import { usePermissions } from "src/pages/features/usePermissions";

const { Text } = Typography;

interface NoticeCardSectionProps {
  binDto: PremierPlusBinDTO | null;
}

interface StopConfirmationContentProps {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const StopConfirmationContent: React.FC<StopConfirmationContentProps> = ({ message, onConfirm, onCancel }) => (
  <>
    <Text>{message}</Text>
    <br />
    <Row justify="center" gutter={12} style={{ paddingTop: 12 }}>

      <Col>
        <Button type="primary" onClick={onCancel}>
          No
        </Button>
      </Col>
      <Col>
        <Button type="primary" onClick={onConfirm}>
          Yes
        </Button>
      </Col>
    </Row>
  </>
);


const NoticeCardSection: React.FC<NoticeCardSectionProps> = ({ binDto }) => {
  const [showFanSettingsModal, setShowFanSettingsModal] = useState(false);
  const [showDryerSettingsModal, setShowDryerSettingsModal] = useState(false);
  const [fanPopoverOpen, setFanPopoverOpen] = useState(false);
  const [augerPopoverOpen, setAugerPopoverOpen] = useState(false);
  const [stopDryerPopoverOpen, setStopDryerPopoverOpen] = useState(false);
  const [sequencedDryerStopPopoverOpen, setSequencedDryerStopOpen] = useState(false);
  const [showHmi, setShowHmi] = useState<ViewHmi | null>(null);

  const showManualModeSection = useMemo (() => {
    return binDto?.isInAutoModePhysically == false;
  }, [binDto]);

  const handleShowFanSettingsModal = () => setShowFanSettingsModal(true);
  const handleCloseFanSettingsModal = () => setShowFanSettingsModal(false);

  const handleShowDryerSettingsModal = () => setShowDryerSettingsModal(true);
  const handleCloseDryerSettingsModal = () => setShowDryerSettingsModal(false);

  const handleClickViewDrying = () => {
    setShowHmi(ViewHmi.Dry);
  }

  const handleClickViewFan = () => {
    setShowHmi(ViewHmi.Fan);
  }

  const handleClickViewMoveGrain = () => {
    setShowHmi(ViewHmi.MoveGrain);
  }

  const handleCloseHmi = () => {
    setShowHmi(null);
  }

  const showNoOperations =
    !binDto?.dryingOperation?.isRunning &&
    binDto?.settings?.fanRunSettings?.fanOperationType === FanOperationType.Off &&
    !binDto?.grainMovingOperation?.isGrainMoving;

  const renderNoOperationsCard = () => {
    const permissions = usePermissions();
    return (<NoticeCard
      title="No Operations are active"
      description="There are no active operations. Only fans can be started remotely. Starting dryer & moving grain must be done on-site on the HMI touch screen"
      buttons={
        <Button disabled={!permissions.canWrite} onClick={handleShowFanSettingsModal} type="primary">
          Run Fans
        </Button>
      }
    />);
    };

  const renderDryerCard = () => {

    const permissions = usePermissions();

    const onClickYesStopDryer = async () => {
      try{
        const result = await BinApiService.premierPlus_StopDryingOperation(binDto?.binId ?? 0);
        notification.success({ message: 'Dryer stopped successfully' });
      }catch(err){
        console.error('Error stopping dryer:', err);
        notification.error({ message: 'Error stopping dryer', description: 'Please try again later.' });
      }finally{
      }

      setStopDryerPopoverOpen(false);
    }

    const onClickNoStopDryer = () => {
      setStopDryerPopoverOpen(false);
    }


    const onClickYesSequencedDryerStop = async () => {
      try{
        const result = await BinApiService.premierPlus_SequencedDryingStop(binDto?.binId ?? 0);
        notification.success({ message: 'Dryer stopped successfully' });
      }catch(err){
        console.error('Error stopping dryer:', err);
        notification.error({ message: 'Error stopping dryer', description: 'Please try again later.' });
      }finally{
      }

      setStopDryerPopoverOpen(false);
    }

    const onClickNoSequencedDryerStop = () => {
      setSequencedDryerStopOpen(false);
    }

    return(
      <NoticeCard
      title="Dryer is running"
      description="Automated drying operations are currently active"
      buttons={
        <>
            <Popover
            open={sequencedDryerStopPopoverOpen}
            trigger="click"
            onOpenChange={(newOpen) => {
              if (!newOpen) setSequencedDryerStopOpen(false);
            }}
            content={
                <StopConfirmationContent
                message="Are you sure you want to stop drying operations?"
                onConfirm={onClickYesSequencedDryerStop}
                onCancel={onClickNoSequencedDryerStop}
                />
            }
          >
            <Button type="primary" disabled={!permissions.canWrite} danger onClick={() => setSequencedDryerStopOpen(true)}>
              Sequenced Dryer Stop
            </Button>
          </Popover>

          <Popover
            open={stopDryerPopoverOpen}
            trigger="click"
            onOpenChange={(newOpen) => {
              if (!newOpen) setStopDryerPopoverOpen(false);
            }}
            content={
                <StopConfirmationContent
                message="Are you sure you want to stop drying operations?"
                onConfirm={onClickYesStopDryer}
                onCancel={onClickNoStopDryer}
                />
            }
          >
            <Button type="primary" disabled={!permissions.canWrite} danger onClick={() => setStopDryerPopoverOpen(true)}>
              Stop All
            </Button>
          </Popover>

          <Button type="primary" onClick={handleShowDryerSettingsModal}>
            Change Settings
          </Button>

          <Button type="primary" onClick={handleClickViewDrying}>
            View HMI 
          </Button>
        </>
      }
    />
    )
  };

  const renderFanCard = () => {

    const permissions = usePermissions();

    const onClickYesStopFans = async () => {
      try{
          let options = binDto?.settings?.fanRunSettings!;
          options.fanOperationType = FanOperationType.Off;

          const request : PremierPlusSyncSettingsRequestDTO = {
            azureDeviceId: binDto?.deviceId ?? '',
            settingsGuid: null,
            isWebRequest: true,
            generalSystemSettings: null,
            dryingOperationsSettings: null,
            grainMovingSettings: null,
            fanRunSettings: options
          }

          const result = await BinApiService.premierPlus_SetSettings(request);
          notification.success({ message: 'Settings successfully set'});
      }catch(err){
        console.error('Error stopping fans:', err);
        notification.error({ message: 'Error stopping fans', description: 'Please try again later.' });
      }

      setFanPopoverOpen(false);
    }

    const onClickNoStopFans = () => {
      setFanPopoverOpen(false)
    }
    
    return(<NoticeCard
      title="Run fans is active"
      description="Automated fan operations are currently active"
      buttons={
        <>
          <Popover
            open={fanPopoverOpen}
            trigger="click"
            onOpenChange={(newOpen) => {
              if (!newOpen) setFanPopoverOpen(false);
            }}
            content={
              <StopConfirmationContent
                message="Are you sure you want to stop all fans?"
                onConfirm={onClickYesStopFans}
                onCancel={onClickNoStopFans}
              />
            }
          >
            <Button type="primary" disabled={!permissions.canWrite} danger onClick={() => setFanPopoverOpen(true)}>
              Stop All Fans
            </Button>
          </Popover>

          <Button type="primary" disabled={!permissions.canWrite} onClick={handleShowFanSettingsModal}>
            Adjust Settings
          </Button>
          
          <Button type="primary" onClick={handleClickViewFan}>
            View HMI 
          </Button>
        </>
      }
    />)
    };

  const renderGrainMoveCard = () => {

    const onClickYesStopAugers = async () => {
      try{
        await BinApiService.premierPlus_StopTransferingGrain(binDto?.binId ?? 0);
        notification.success({ message: 'Augers stopped successfully' });
      }catch(err){
        console.error('Error stopping augers:', err);
        notification.error({ message: 'Error stopping augers', description: 'Please try again later.' });
      }finally{
      }
    }


    const onClickNoStopAugers = () => {
      setAugerPopoverOpen(false);
    };
    
    return(<NoticeCard
      title="Move grain is active"
      description="Grain is currently moving to Bin 2"
      buttons={
        <>
          <Popover
            open={augerPopoverOpen}
            trigger="click"
            onOpenChange={(newOpen) => {
              if (!newOpen) setAugerPopoverOpen(false);
            }}
            content={
                <StopConfirmationContent
                message="Are you sure you want to stop all augers?"
                onConfirm={onClickYesStopAugers}
                onCancel={onClickNoStopAugers}
                />
            }
          >
            <Row justify={'center'} gutter={8}>
              <Col>
                <Button type="primary" danger onClick={() => setAugerPopoverOpen(true)}>
                  Stop All Augers
                </Button>
              </Col>

              <Col>
                <Button type="primary" onClick={handleClickViewMoveGrain}>
                  View HMI 
                </Button> 
              </Col>
            </Row>
          </Popover>
        </>
      }
    />);
    };

  const renderManualModeCard = () => {

    return(<NoticeCard
      title=" Manual Mode Activated"
      description="Bin is physically switched to manual mode. All remote controls disabled."
      />
    );
  }

  return (
    <>
      {
        showManualModeSection ? 
        renderManualModeCard() : 
        !showNoOperations ? (
          <>
            {binDto?.dryingOperation?.isRunning && renderDryerCard()}
            {binDto?.dryingOperation?.isRunning !== true && binDto?.settings?.fanRunSettings?.fanOperationType !== FanOperationType.Off && renderFanCard()}
            <br />
            {binDto?.dryingOperation?.isRunning !== true && binDto?.grainMovingOperation?.isGrainMoving && renderGrainMoveCard()}
          </>
        ) : renderNoOperationsCard()
      }
      <PremierPlusFanSettingsModal
        open={showFanSettingsModal}
        onClose={handleCloseFanSettingsModal}
        onCancel={handleCloseFanSettingsModal}
        centered
        destroyOnClose
        binDto={binDto}
      />
      <PremierPlusDryerSettingsModal
        open={showDryerSettingsModal}
        onClose={handleCloseDryerSettingsModal}
        onCancel={handleCloseDryerSettingsModal}
        centered
        destroyOnClose
        binDto={binDto}
      />

      <HmiViewModal 
      open={showHmi != null} 
      binDto={binDto} 
      onClose={handleCloseHmi}
      onCancel={handleCloseHmi}
      view={showHmi}
      />
    </>
  );
};

export default NoticeCardSection;
